import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  BarChart3, Clock, MapPin, TrendingUp, TrendingDown, 
  CheckCircle, XCircle, SkipForward, AlertTriangle, Loader2
} from 'lucide-react';

const statusIcons = {
  completed: { icon: CheckCircle, color: 'text-green-500' },
  successful: { icon: CheckCircle, color: 'text-green-500' },
  unsuccessful: { icon: XCircle, color: 'text-red-500' },
  skipped: { icon: SkipForward, color: 'text-yellow-500' },
  pending: { icon: Clock, color: 'text-slate-400' },
};

export default function RouteAnalyticsDialog({ open, onOpenChange, routeId }) {
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    if (open && routeId) {
      loadAnalytics();
    }
  }, [open, routeId]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const res = await api.getRouteAnalytics(routeId);
      setAnalytics(res.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Failed to load route analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes && minutes !== 0) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const formatVariance = (minutes) => {
    if (minutes === null || minutes === undefined) return '-';
    
    const roundedMins = Math.round(minutes);
    const absMinutes = Math.abs(roundedMins);
    const hours = Math.floor(absMinutes / 60);
    const mins = absMinutes % 60;
    
    // Show sign: + for late, - for early
    const sign = roundedMins > 0 ? '+' : (roundedMins < 0 ? '-' : '');
    
    if (absMinutes === 0) {
      return '-'; // Exactly on time
    }
    
    if (hours > 0) {
      return mins > 0 ? `${sign}${hours}h ${mins}m` : `${sign}${hours}h`;
    }
    return `${sign}${mins}m`;
  };

  const formatTime12Hour = (timeStr) => {
    if (!timeStr) return '-';
    
    // If it's already in "H:MM AM/PM" format, return as-is
    if (timeStr.includes('AM') || timeStr.includes('PM')) {
      return timeStr;
    }
    
    // If it's HH:MM format (24-hour), convert to 12-hour
    if (timeStr.includes(':')) {
      const parts = timeStr.split(':');
      const hours = parseInt(parts[0], 10);
      const minutes = parseInt(parts[1], 10);
      
      if (isNaN(hours) || isNaN(minutes)) return timeStr;
      
      const period = hours >= 12 ? 'PM' : 'AM';
      const hour12 = hours % 12 || 12;
      return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
    }
    
    return timeStr;
  };

  const getVarianceColor = (variance) => {
    if (!variance && variance !== 0) return 'text-slate-400';
    if (variance < 0) return 'text-green-500';  // Early = win
    if (variance > 0) return 'text-red-500';    // Late = loss
    return 'text-slate-400';                     // Exactly 0 = wash
  };

  if (!analytics && !loading) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Route Analytics
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        ) : analytics ? (
          <div className="space-y-6">
            {/* Route Header */}
            <div className="bg-slate-800/50 rounded-lg p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-semibold text-white">{analytics.route_name || 'Route'}</h3>
                  <p className="text-sm text-slate-400">{analytics.route_date}</p>
                </div>
                <Badge className={`${
                  analytics.status === 'completed' ? 'bg-green-600' : 
                  analytics.status === 'active' ? 'bg-blue-600' : 'bg-slate-600'
                } text-white`}>
                  {analytics.status}
                </Badge>
              </div>
            </div>

            {/* OVERALL ANALYTICS SECTION */}
            <div>
              <h4 className="text-sm font-semibold text-teal-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Overall Performance
              </h4>
              
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Completion Rate */}
              <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-white">
                  {analytics.summary.completion_rate}%
                </p>
                <p className="text-sm text-slate-400">Completion Rate</p>
                <p className="text-xs text-slate-500 mt-1">
                  {analytics.summary.completed_stops}/{analytics.summary.total_stops} stops
                </p>
              </div>

              {/* Stop Status Breakdown */}
              <div className="bg-slate-800/50 rounded-lg p-4">
                <p className="text-sm text-slate-400 mb-2">Stop Status</p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-green-400">Completed</span>
                    <span className="text-white">{analytics.summary.completed_stops}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-red-400">Unsuccessful</span>
                    <span className="text-white">{analytics.summary.unsuccessful_stops}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-yellow-400">Skipped</span>
                    <span className="text-white">{analytics.summary.skipped_stops}</span>
                  </div>
                  {analytics.summary.pending_stops > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Pending</span>
                      <span className="text-white">{analytics.summary.pending_stops}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Time Analysis */}
              <div className="bg-slate-800/50 rounded-lg p-4">
                <p className="text-sm text-slate-400 mb-2">Time Analysis</p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Estimated</span>
                    <span className="text-white">{formatDuration(analytics.time_analysis.estimated_duration_minutes)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Actual</span>
                    <span className="text-white">{formatDuration(analytics.time_analysis.actual_duration_minutes)}</span>
                  </div>
                  <div className="flex justify-between border-t border-slate-700 pt-1 mt-1">
                    <span className="text-slate-400">Variance</span>
                    <span className={getVarianceColor(analytics.time_analysis.duration_variance_minutes)}>
                      {formatVariance(analytics.time_analysis.duration_variance_minutes)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Punctuality - Simple: Early or Late */}
              <div className="bg-slate-800/50 rounded-lg p-4">
                <p className="text-sm text-slate-400 mb-2">Punctuality</p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-green-400">Left Early</span>
                    <span className="text-white">{analytics.time_analysis.early_stops}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-red-400">Left Late</span>
                    <span className="text-white">{analytics.time_analysis.late_stops}</span>
                  </div>
                  <div className="flex justify-between border-t border-slate-700 pt-1 mt-1">
                    <span className="text-slate-400">Avg Variance</span>
                    <span className={getVarianceColor(analytics.time_analysis.average_stop_variance_minutes)}>
                      {formatVariance(analytics.time_analysis.average_stop_variance_minutes)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Distance Analysis - Only show when this route has BOTH estimated AND actual GPS data */}
            {analytics.distance_analysis?.actual_distance_miles > 0 && analytics.distance_analysis?.estimated_distance_miles > 0 && (
              <div className="bg-slate-800/50 rounded-lg p-4 mt-4">
                <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Mileage Tracking
                </h4>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-white">
                      {analytics.distance_analysis.estimated_distance_miles} mi
                    </p>
                    <p className="text-sm text-slate-400">Estimated</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">
                      {analytics.distance_analysis.actual_distance_miles} mi
                    </p>
                    <p className="text-sm text-slate-400">Actual (GPS)</p>
                  </div>
                  <div>
                    <p className={`text-2xl font-bold ${
                      analytics.distance_analysis.distance_variance_miles > 0 
                        ? 'text-red-400' 
                        : analytics.distance_analysis.distance_variance_miles < 0 
                          ? 'text-green-400' 
                          : 'text-slate-400'
                    }`}>
                      {analytics.distance_analysis.distance_variance_miles > 0 ? '+' : ''}
                      {analytics.distance_analysis.distance_variance_miles} mi
                    </p>
                    <p className="text-sm text-slate-400">Difference</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Close Overall section, add spacing */}
            </div>

            {/* PER-STOP ANALYTICS SECTION */}
            <div>
              <h4 className="text-sm font-semibold text-purple-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Per-Stop Analysis
              </h4>

              {/* Stop Details Table */}
              <div className="bg-slate-800/50 rounded-lg p-4">
                <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-slate-400 border-b border-slate-700">
                      <th className="text-left py-2 px-2">#</th>
                      <th className="text-left py-2 px-2">Defendant</th>
                      <th className="text-left py-2 px-2">Address</th>
                      <th className="text-center py-2 px-2">Status</th>
                      <th className="text-center py-2 px-2">Est. Arrival</th>
                      <th className="text-center py-2 px-2">Actual</th>
                      <th className="text-center py-2 px-2">Variance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.stops.map((stop) => {
                      const StatusIcon = statusIcons[stop.status]?.icon || Clock;
                      const statusColor = statusIcons[stop.status]?.color || 'text-slate-400';
                      
                      return (
                        <tr key={stop.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                          <td className="py-2 px-2 text-slate-300">{stop.order + 1}</td>
                          <td className="py-2 px-2 text-white font-medium">{stop.defendant_name}</td>
                          <td className="py-2 px-2 text-slate-400 text-xs truncate max-w-[200px]">{stop.address}</td>
                          <td className="py-2 px-2 text-center">
                            <StatusIcon className={`h-4 w-4 ${statusColor} inline`} />
                          </td>
                          <td className="py-2 px-2 text-center text-slate-300">{formatTime12Hour(stop.estimated_arrival)}</td>
                          <td className="py-2 px-2 text-center text-slate-300">
                            {stop.actual_completion 
                              ? formatTime12Hour(stop.actual_completion)
                              : '-'
                            }
                          </td>
                          <td className={`py-2 px-2 text-center font-medium ${getVarianceColor(stop.variance_minutes)}`}>
                            {formatVariance(stop.variance_minutes)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-4 text-xs text-slate-500">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                Early
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                Late
              </span>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
